import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React from 'react';
import { IconChevronSprite } from '$partials/icons/index.js';
import { Button } from '$partials/ui';
import Image from '$partials/media/image';
import { useLocale } from '$contexts/locale.jsx';
import { isLocalURL } from '$utilities/browser';
gsap.registerPlugin(ScrollTrigger);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleFeature}
 */

const ModuleFeature = ({
    eyebrow,
    heading,
    bodyCopy,
    ctaLink,
    ctaLabel,
    media,
    // @ts-ignore — injected by dispatcher, not part of the CMS fragment
    previousTypename,
    // @ts-ignore — injected by dispatcher, not part of the CMS fragment
    isArticlePage,
}) => {

    const {
        formatURI,
        translate: t,
    } = useLocale();

    const [isHovered, setIsHovered] = React.useState(false);
    const [isButtonExiting, setIsButtonExiting] = React.useState(false);
    const exitTimerRef = React.useRef(/** @type {ReturnType<typeof setTimeout> | undefined} */ (undefined));
    const hovered = isHovered && media?.src;
    const buttonRef = React.useRef(/** @type {HTMLAnchorElement | null} */ (null));

    React.useEffect(() => () => clearTimeout(exitTimerRef.current), []);

    const collapseTopPadding = previousTypename === 'BvaModuleSingleImage';
    const topPaddingClass = collapseTopPadding ? ' pt-16' : ` pt-40${isArticlePage ? '' : ' md:pt-80'}`;

    // render
    return (
        <section className={`module-feature relative pb-40${isArticlePage ? '' : ' md:pb-80'}${topPaddingClass}`}>
            <div className='relative z-10 layout-container layout-site-outer-padding w-full overflow-hidden'>

                <div
                    className={`min-h-[432px] relative pb-16 sm:pb-32 px-32 md:px-40 pt-50 md:py-120 bg-theme-themed-surface-bg rounded overflow-hidden${ctaLink ? ' cursor-pointer' : ''}`}
                    onClick={(e) => {
                        if (!ctaLink || buttonRef.current?.contains(/** @type {Node} */ (e.target))) {
                            return;
                        }
                        const uri = formatURI(ctaLink);
                        if (isLocalURL(uri)) {
                            window.location.href = uri;
                        } else {
                            window.open(uri, '_blank', 'noopener,noreferrer');
                        }
                    }}
                    onMouseEnter={() => { setIsHovered(true); setIsButtonExiting(false); clearTimeout(exitTimerRef.current); }}
                    onMouseLeave={() => {
                        setIsHovered(false);
                        setIsButtonExiting(true);
                        exitTimerRef.current = setTimeout(() => setIsButtonExiting(false), 700);
                    }}>
                    {media?.src && (
                        <div
                            className='absolute inset-0 transition-opacity duration-300'
                            style={{
                                opacity: isHovered ? 1 : 0,
                                backgroundImage: `linear-gradient(96deg, rgba(0, 0, 0, 0.30) 0.08%, rgba(0, 0, 0, 0.00) 99.92%), url(${media.src})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }} />
                    )}
                    <div className='flex flex-col relative'>
                        <div className='flex flex-col md:flex-row justify-between gap-y-32 md:items-center'>
                            <div className='w-full sm:w-11/12 md:w-7/12 flex flex-col justify-center'>
                                {eyebrow && (
                                    <p
                                        className='type-style-eyebrow text-text-themed-text-accent mb-16 transition-colors duration-300'
                                        style={hovered ? { color: '#fff' } : undefined}>
                                        {eyebrow}
                                    </p>
                                )}

                                {/* hero text */}
                                {heading && (
                                    <div
                                        className={`w-full ${isArticlePage ? 'type-style-4' : 'type-style-3-extended'} break-words text-secondary transition-colors duration-300`}
                                        style={hovered ? { color: '#fff' } : undefined}>
                                        {heading}
                                    </div>
                                )}

                                {/* body copy */}
                                {bodyCopy && (
                                    <p
                                        className='text-text-secondary type-style-body mt-16 transition-colors duration-300'
                                        style={hovered ? { color: '#fff' } : undefined}>
                                        {bodyCopy}
                                    </p>
                                )}
                            </div>

                            {/* cta buttons */}
                            {ctaLink && (
                                <Button
                                    as='a'
                                    className={`button button--primary sm:max-md:ml-auto ${!ctaLabel ? 'h-60 w-60' : 'w-max'}${isHovered ? ' is-label-entering' : ''}${isButtonExiting ? ' is-label-exiting' : ''}`}
                                    href={formatURI(ctaLink)}
                                    ref={buttonRef}>
                                    {ctaLabel &&(
                                        <span className='button__label--animate'>
                                            <span>
                                                {ctaLabel}
                                            </span>
                                        </span>
                                    )}
                                    <span
                                        className='button__icon button__icon--animate'
                                        style={!ctaLabel ? { 'marginLeft': '0px' } : undefined}>
                                        <IconChevronSprite />
                                    </span>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ModuleFeature;
